import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useNavigation } from "./NavigationContext";
import { simulationService } from "../services/api/simulationService";
const ExportedDataContext = createContext();

export const useExportedData = () => {
  const context = useContext(ExportedDataContext);
  if (!context) {
    throw new Error(
      "useExportedData must be used within an ExportedDataProvider",
    );
  }
  return context;
};

export const ExportedDataProvider = ({ children }) => {
  const {
    timeSeriesFileName,
    setTimeSeriesFileName,
    storageAccountName,
    setStorageAccountName,
    selectedBranchId,
  } = useNavigation();

  // State for all exported data
  const [exportedData, setExportedDataState] = useState({
    totalAreaUnderCurve: null,
    view: "yearly",
    selectedPeriod: "2025",
  });
  const [overallExportedData, setOverallExportedDataState] = useState({
    totalAreaUnderCurve: null,
    view: "yearly",
    selectedPeriod: "2025",
  });

  const [exportedTransferData, setExportedTransferDataState] = useState({
    totalImport: "0.00",
    totalExport: "0.00",
    minPower: "0.00",
    maxPower: "0.00",
    value: "0.00",
    view: "yearly",
    selectedPeriod: "2025",
  });
  const [overallExportedTransferData, setOverallExportedTransferDataState] =
    useState({
      totalImport: "0.00",
      totalExport: "0.00",
      minPower: "0.00",
      maxPower: "0.00",
      value: "0.00",
      view: "yearly",
      selectedPeriod: "2025",
    });

  const [exportedStorageData, setExportedStorageDataState] = useState([]);
  const [exportedStorageSummaryData, setExportedStorageSummaryDataState] =
    useState({
      totalEnergy: "0.00",
      chargeTotal: "0.00",
      dischargeTotal: "0.00",
      minPower: "0.00",
      maxPower: "0.00",
      value: "0.00",
      avgSOC: "0.00",
      view: "yearly",
      selectedPeriod: "2025",
    });
  const [
    overallExportedStorageSummaryData,
    setOverallExportedStorageSummaryDataState,
  ] = useState({
    totalEnergy: "0.00",
    chargeTotal: "0.00",
    dischargeTotal: "0.00",
    minPower: "0.00",
    maxPower: "0.00",
    value: "0.00",
    avgSOC: "0.00",
    view: "yearly",
    selectedPeriod: "2025",
  });

  const [exportedProductionData, setExportedProductionDataState] = useState([]);
  const [exportedProductionSummaryData, setExportedProductionSummaryDataState] =
    useState({
      totalProduction: "0.00",
      maxPower: "0.00",
      totalCost: "0.00",
      view: "yearly",
      selectedPeriod: "2025",
    });
  const [
    overallExportedProductionSummaryData,
    setOverallExportedProductionSummaryDataState,
  ] = useState({
    totalProduction: "0.00",
    maxPower: "0.00",
    totalCost: "0.00",
    view: "yearly",
    selectedPeriod: "2025",
  });

  /* The per-category split of the demand charge. Absent is not zero: a result
   * predating the sim's attribution columns has hasAttribution false and null
   * values, and the cards show an em-dash rather than a fabricated $0.00. */
  const demandChargeDefaults = {
    value: "0.00",
    peakPower: "0.00",
    hasDemandCharge: false,
    hasAttribution: false,
    productionDemand: null,
    consumptionDemand: null,
    storageDemand: null,
    unattributedDemand: null,
    view: "yearly",
    selectedPeriod: "2025",
  };

  const [exportedDemandChargeData, setExportedDemandChargeDataState] =
    useState(demandChargeDefaults);
  const [
    overallExportedDemandChargeData,
    setOverallExportedDemandChargeDataState,
  ] = useState(demandChargeDefaults);

  // Track when each chart has set its data
  const [chartDataReadyStates, setChartDataReadyStates] = useState({
    transfer: false,
    production: false,
    consumption: false,
    storage: false,
    pricing: false,
  });

  // Add simulation data state
  const [simulations, setSimulations] = useState([]);
  const [simulationDataLoading, setSimulationDataLoading] = useState(false);
  const [simulationError, setSimulationError] = useState(null);

  // Create setters that work like normal useState setters but also update ready state
  const setExportedData = useCallback((data) => {
    setExportedDataState(data);
    setChartDataReadyStates((prev) => ({ ...prev, consumption: true }));
  }, []);
  const setOverallExportedData = useCallback((data) => {
    setOverallExportedDataState(data);
  }, []);

  const setExportedTransferData = useCallback((data) => {
    setExportedTransferDataState(data);
    setChartDataReadyStates((prev) => ({ ...prev, transfer: true }));
  }, []);
  const setOverallExportedTransferData = useCallback((data) => {
    setOverallExportedTransferDataState(data);
  }, []);

  const setExportedStorageData = useCallback((data) => {
    setExportedStorageDataState(data);
    setChartDataReadyStates((prev) => ({ ...prev, storage: true }));
  }, []);

  const setExportedStorageSummaryData = useCallback((data) => {
    setExportedStorageSummaryDataState(data);
  }, []);
  const setOverallExportedStorageSummaryData = useCallback((data) => {
    setOverallExportedStorageSummaryDataState(data);
  }, []);

  const setExportedProductionData = useCallback((data) => {
    setExportedProductionDataState(data);
    setChartDataReadyStates((prev) => ({ ...prev, production: true }));
  }, []);

  const setExportedProductionSummaryData = useCallback((data) => {
    setExportedProductionSummaryDataState(data);
  }, []);
  const setOverallExportedProductionSummaryData = useCallback((data) => {
    setOverallExportedProductionSummaryDataState(data);
  }, []);

  const setExportedDemandChargeData = useCallback((data) => {
    setExportedDemandChargeDataState(data);
  }, []);
  const setOverallExportedDemandChargeData = useCallback((data) => {
    setOverallExportedDemandChargeDataState(data);
  }, []);

  const setExportedPricingData = useCallback((data) => {
    // Pricing doesn't have data to set, but mark as ready
    setChartDataReadyStates((prev) => ({ ...prev, pricing: true }));
  }, []);

  const formatEmailForKey = (email) => email.replace(/[@.]/g, "-");

  const getAllResultsFromResponse = (jsonData) => {
   const selectedCustomerEmail =
  sessionStorage.getItem("selectedCustomerEmail") ||
  sessionStorage.getItem("email");
    if (!selectedCustomerEmail) return [];
    const formattedEmail = formatEmailForKey(selectedCustomerEmail);
    if (jsonData[formattedEmail] && jsonData[formattedEmail].results) {
      return jsonData[formattedEmail].results;
    }
    return [];
  };

  const groupResultsByLast4Digits = (resultsArray) => {
    const grouped = {};
    resultsArray.forEach((fileObj) => {
      const { name, created } = fileObj;
      if (
        name.startsWith("economic_summary_") ||
        name.startsWith("timeseries_") ||
        name.startsWith("daily_summary_")
      ) {
        const match = name.match(/(\d{4})\.csv$/);
        if (!match) return;
        const last4 = match[1];
        if (!grouped[last4]) {
          grouped[last4] = {
            timeKey: last4,
            createdAt: null,
            economicSummary: null,
            timeseries: null,
            dailySummary: null,
          };
        }
        if (name.startsWith("timeseries_")) {
          grouped[last4].timeseries = fileObj;
        } else if (name.startsWith("daily_summary_")) {
          grouped[last4].dailySummary = fileObj;
        } else if (name.startsWith("economic_summary_")) {
          grouped[last4].economicSummary = fileObj;
        }
        grouped[last4].createdAt = created;
      }
    });

    let simulations = Object.values(grouped).filter(
      (sim) => sim.timeseries && sim.dailySummary,
    );
    simulations.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return simulations;
  };

  const fetchStorageAccountName = useCallback(async (branchId) => {
    try {
      const data = await simulationService.getStorageAccountName(branchId);
      const newAccountName = data.storage_account?.storage_account_name;

      if (newAccountName) {
        setStorageAccountName(newAccountName);
      }

      return newAccountName;
    } catch (error) {
      setSimulationError(error.message);
      return null;
    }
  }, [setStorageAccountName]);

  // Fetch simulations data
  const fetchSimulations = useCallback(async () => {
    const email = sessionStorage.getItem("selectedCustomerEmail");
    const token = sessionStorage.getItem("accessToken");
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const simulationName =
      email?.replace(/[@.]/g, "-") || "default-simulation-name";

   

    if (!email || !token) {
      
      return [];
    }

    // Fetch storage account if not available
    let currentStorageAccount = storageAccountName;
    if (!currentStorageAccount) {
      currentStorageAccount = await fetchStorageAccountName(selectedBranchId);
      if (!currentStorageAccount) {
        return [];
      }
    }

    setSimulationDataLoading(true);
    setSimulationError(null);

    try {
      const data = await simulationService.getList({ customerName: currentStorageAccount, accessToken: token });
      const allResults = getAllResultsFromResponse(data);
      const groupedSimulations = groupResultsByLast4Digits(allResults);

      if (!timeSeriesFileName && groupedSimulations[0]?.timeseries?.name) {
        setTimeSeriesFileName(groupedSimulations[0].timeseries.name);
      }

      setSimulations(groupedSimulations);
      return groupedSimulations;
    } catch (error) {
      
      setSimulationError(error.message);
      return [];
    } finally {
      setSimulationDataLoading(false);
    }
  }, [storageAccountName, fetchStorageAccountName]);

  // Initialize and fetch simulations when component mounts
  React.useEffect(() => {
    const email = sessionStorage.getItem("selectedCustomerEmail")|| sessionStorage.getItem("email");
    const token = sessionStorage.getItem("accessToken");

    
    

    if (email && token && !timeSeriesFileName) {
      fetchSimulations();
    }
  }, [fetchSimulations]);

  React.useEffect(() => {
    if (
      simulations.length > 0 &&
      !timeSeriesFileName
    ) {
      const latestSimulation = simulations[0];
      if (latestSimulation?.timeseries?.name) {
        setTimeSeriesFileName(latestSimulation.timeseries.name);
      }
    }
  }, [simulations, timeSeriesFileName, setTimeSeriesFileName]);

  // Check if there's meaningful data (non-zero values) in exported data
  const hasMeaningfulData = useMemo(() => {
    // Check Transfer data for meaningful values
    const hasTransferData =
      exportedTransferData &&
      ((exportedTransferData.totalEnergy &&
        parseFloat(exportedTransferData.totalEnergy) > 0) ||
        (exportedTransferData.minPower &&
          Math.abs(parseFloat(exportedTransferData.minPower)) > 0) ||
        (exportedTransferData.maxPower &&
          parseFloat(exportedTransferData.maxPower) > 0) ||
        /* Magnitude, not sign. A transfer cost is negative in the engine's
         * convention, so `> 0` was false for every real importing site and this
         * clause could only ever fire on an exporter. It is an OR arm of a
         * readiness widener, so it never blocked anything -- minPower carried
         * it -- but it did not do what it reads as. */
        (exportedTransferData.value &&
          Math.abs(parseFloat(exportedTransferData.value)) > 0));

    // Check Production data for meaningful values
    const hasProductionData =
      exportedProductionSummaryData &&
      ((exportedProductionSummaryData.totalProduction &&
        parseFloat(exportedProductionSummaryData.totalProduction) > 0) ||
        (exportedProductionSummaryData.maxPower &&
          parseFloat(exportedProductionSummaryData.maxPower) > 0) ||
        (exportedProductionSummaryData.totalCost &&
          Math.abs(parseFloat(exportedProductionSummaryData.totalCost)) > 0));

    // Check Consumption data for meaningful values
    const hasConsumptionData =
      exportedData &&
      exportedData.totalAreaUnderCurve &&
      Math.abs(parseFloat(exportedData.totalAreaUnderCurve)) > 0;

    // Check Storage data for meaningful values
    const hasStorageData =
      exportedStorageSummaryData &&
      ((exportedStorageSummaryData.totalEnergy &&
        parseFloat(exportedStorageSummaryData.totalEnergy) > 0) ||
        (exportedStorageSummaryData.minPower &&
          Math.abs(parseFloat(exportedStorageSummaryData.minPower)) > 0) ||
        (exportedStorageSummaryData.maxPower &&
          parseFloat(exportedStorageSummaryData.maxPower) > 0) ||
        (exportedStorageSummaryData.value &&
          Math.abs(parseFloat(exportedStorageSummaryData.value)) > 0));

    return (
      hasTransferData ||
      hasProductionData ||
      hasConsumptionData ||
      hasStorageData
    );
  }, [
    exportedTransferData,
    exportedProductionData,
    exportedProductionSummaryData,
    exportedData,
    exportedStorageData,
    exportedStorageSummaryData,
  ]);

  // Check if cards should be shown
  const shouldShowCards = useMemo(() => {
    return (
      hasMeaningfulData &&
      (chartDataReadyStates.transfer ||
        chartDataReadyStates.production ||
        chartDataReadyStates.consumption ||
        chartDataReadyStates.storage)
    );
  }, [hasMeaningfulData, chartDataReadyStates]);

  const value = useMemo(
    () => ({
      // Data (read-only)
      exportedData,
      overallExportedData,
      exportedTransferData,
      overallExportedTransferData,
      exportedStorageData,
      exportedStorageSummaryData,
      overallExportedStorageSummaryData,
      exportedProductionData,
      exportedProductionSummaryData,
      overallExportedProductionSummaryData,
      exportedDemandChargeData,
      overallExportedDemandChargeData,
      chartDataReadyStates,

      // Setters that work like normal useState setters
      setExportedData,
      setOverallExportedData,
      setExportedTransferData,
      setOverallExportedTransferData,
      setExportedStorageData,
      setExportedStorageSummaryData,
      setOverallExportedStorageSummaryData,
      setExportedProductionData,
      setExportedProductionSummaryData,
      setOverallExportedProductionSummaryData,
      setExportedDemandChargeData,
      setOverallExportedDemandChargeData,
      setExportedPricingData,

      // Helper functions
      hasMeaningfulData,
      shouldShowCards,
    }),
    [
      exportedData,
      overallExportedData,
      exportedTransferData,
      overallExportedTransferData,
      exportedStorageData,
      exportedStorageSummaryData,
      overallExportedStorageSummaryData,
      exportedProductionData,
      exportedProductionSummaryData,
      overallExportedProductionSummaryData,
      exportedDemandChargeData,
      overallExportedDemandChargeData,
      chartDataReadyStates,
      setExportedData,
      setOverallExportedData,
      setExportedTransferData,
      setOverallExportedTransferData,
      setExportedStorageData,
      setExportedStorageSummaryData,
      setOverallExportedStorageSummaryData,
      setExportedProductionData,
      setExportedProductionSummaryData,
      setOverallExportedProductionSummaryData,
      setExportedDemandChargeData,
      setOverallExportedDemandChargeData,
      setExportedPricingData,
      hasMeaningfulData,
      shouldShowCards,
    ],
  );

  return (
    <ExportedDataContext.Provider value={value}>
      {children}
    </ExportedDataContext.Provider>
  );
};
